import * as React from 'react'

import { useLocalStorageState } from '@/lib/use-local-storage-state'
import { ASSISTANT_WIDTH } from '@/modules/assistant'
import { useAssistantBrowserCommand } from '@/lib/assistant-browser-commands'
import { assistantBrowserCommands } from '@/lib/assistant-browser-commands.generated'

const SIDEBAR_KEY = 'sidebar:collapsed'
const SIDEBAR_WIDTH_KEY = 'sidebar:width'
const ASSISTANT_WIDTH_KEY = 'assistant:width'

export const SIDEBAR_WIDTH = {
  collapsed: 48,
  collapseAt: 120,
  expandAt: 160,
  min: 192,
  max: 384,
  initial: 192,
}

export const ASSISTANT_INSET_CLASS =
  'md:right-[var(--assistant-inset,0px)] transition-[right] duration-300 ease-drawer [.assistant-resizing_&]:transition-none'

type AppContextValue = {
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
  sidebarWidth: number
  setSidebarWidth: (width: number) => void
  assistantOpen: boolean
  setAssistantOpen: (open: boolean) => void
  assistantWidth: number
  setAssistantWidth: (width: number) => void
  assistantToggleRef: React.RefObject<HTMLButtonElement | null>
}

const AppContext = React.createContext<AppContextValue | null>(null)

const parseBool = (raw: string) =>
  raw === 'true' ? true : raw === 'false' ? false : undefined

const parseWidth = (raw: string, min: number, max: number) => {
  const value = Number(raw)
  return Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : undefined
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorageState(
    SIDEBAR_KEY,
    parseBool,
    String,
    true,
  )
  const [sidebarWidth, setSidebarWidth] = useLocalStorageState(
    SIDEBAR_WIDTH_KEY,
    (raw) => parseWidth(raw, SIDEBAR_WIDTH.min, SIDEBAR_WIDTH.max),
    String,
    SIDEBAR_WIDTH.initial,
  )
  const [assistantOpen, setAssistantOpen] = React.useState(false)
  const [assistantWidth, setAssistantWidth] = useLocalStorageState(
    ASSISTANT_WIDTH_KEY,
    (raw) => parseWidth(raw, ASSISTANT_WIDTH.min, ASSISTANT_WIDTH.max),
    String,
    ASSISTANT_WIDTH.initial,
  )
  const assistantToggleRef = React.useRef<HTMLButtonElement>(null)

  useAssistantBrowserCommand(assistantBrowserCommands.sidebar, ({ collapsed }) => {
    setSidebarCollapsed(collapsed)
    return true
  })

  React.useLayoutEffect(() => {
    const root = document.documentElement.style
    root.setProperty('--assistant-width', `${assistantWidth}px`)
    root.setProperty('--assistant-inset', assistantOpen ? `${assistantWidth}px` : '0px')
    return () => {
      root.removeProperty('--assistant-width')
      root.removeProperty('--assistant-inset')
    }
  }, [assistantOpen, assistantWidth])

  return (
    <AppContext.Provider
      value={{
        sidebarCollapsed,
        setSidebarCollapsed,
        sidebarWidth,
        setSidebarWidth,
        assistantOpen,
        setAssistantOpen,
        assistantWidth,
        setAssistantWidth,
        assistantToggleRef,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export function useAppContext() {
  const ctx = React.useContext(AppContext)
  if (!ctx) throw new Error('useAppContext must be used within AppProvider')
  return ctx
}
